<template>
  <div class="div-container">
    <validation-observer ref="refFormObserver">
      <!-- Profile Selection -->
      <b-row>
        <b-col>
          <validation-provider
            #default="validationContext"
            :name="$tc('profiles.title', 0)"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('profiles.title', 0)"
              label-for="id_profile"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_profile"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="profileOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="id_profile"
                data-cy="automatest-wizard-profile-select"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <!-- Permissions TreeSelect -->
      <b-row class="mt-1">
        <b-col>
          <treeselect
            v-model="selectedPermissions"
            :value-consists-of="valueConsistsOf"
            :disabled="!itemData.id_profile"
            :multiple="true"
            :options="permissionsOptions"
            :flat="false"
            :nested="true"
            :always-open="true"
            :max-height="250"
            :limit="3"
          />
        </b-col>
      </b-row>

      <!-- User App Modules Section -->
      <b-row class="mt-20">
        <div :class="{ 'app-modules-wrapper': userGroup }">
          <b-col>
            <b-form-group
              class="text-capitalize"
              :label="$tc('app_modules.title', 2)"
              label-for="user-app-modules"
            >
              <b-form-checkbox-group
                id="user-app-modules"
                v-model="itemData.app_modules"
                :options="appModulesOptions"
                switches
                stacked
              ></b-form-checkbox-group>
            </b-form-group>
          </b-col>
        </div>
      </b-row>
    </validation-observer>
    <div  class="belongGroup" v-if="userGroup">
      <span class="belongGroup-text"
      v-if="itemData.groups[0].name"
      @click="$can('update', 'Group') ? openGroup(itemData.groups[0].id) : null"
    >
        {{ $t("users.belogs_user") + itemData.groups[0].name}}
      </span>
    </div>
  </div>
</template>


<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
} from "@vue/composition-api";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import formValidation from "@core/comp-functions/forms/form-validation";
import store from "@/store";
import { avatarText } from '@core/utils/filter';
import {
  BFormGroup,
  BFormCheckboxGroup,
  BRow,
  BCol,
} from "bootstrap-vue";

export default {
  components: {
    Treeselect,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    BFormGroup,
    BFormCheckboxGroup,
    BRow,
    BCol,
  },
  props: {
    propItemData: {
      type: Object,
      required: false,
    },
    profileOptions: {
      type: Array,
      required: true,
    },
    userGroup: Boolean,
  },
  setup(props, { emit }) {
    const isUpdatingFromProps = ref(false);
    const { refFormObserver, getValidationState } = formValidation();
    const widgetChilds = ref([]);

    // Initialize itemData from propItemData
    const itemData = ref(JSON.parse(JSON.stringify(props.propItemData)));

    // Checking that itemData.app_modules is reactive and initialized
    if (!itemData.value.app_modules) {
      itemData.value.app_modules = [];
    }

    const selectedPermissions = ref(
      itemData.value.permission ? itemData.value.permission : []
    );

    const valueConsistsOf = ref("LEAF_PRIORITY");
    const allSelected = computed(() => {
      const allIds = permissionsOptions.value.flatMap((subject) =>
        subject.children.map((child) => child.id)
      );
      return allIds.every((id) => selectedPermissions.value.includes(id));
    });

    // Fetch permissions from store
    store.dispatch("permission/getAll");
    const permissionsFromStore = computed(() => {
      return store.getters["permission/getItems"];
    });

    const permissionsOptions = computed(() => {
      const subjects = new Map();
      let workspaceVisualMenuWidgetsChild = null;

      // Add each permission depending on the subject (resource)
      permissionsFromStore.value.forEach((permission) => {
        if (!subjects.has(permission.subject)) {
          subjects.set(permission.subject, {
            id: permission.subject,
            label: permission.subject,
            children: [],
          });
        }
        const subject = subjects.get(permission.subject);
        subject.children.push({
          id: permission.id,
          label: permission.description,
          isDisabled: true,
        });
      });

      // Order children alphabetically
      subjects.forEach((subject) => {
        subject.children.sort((a, b) => a.label.localeCompare(b.label));
      });

      // Search for the Workspace and its child
      subjects.forEach((subject, key) => {
        if (key === "Workspace") {
          subject.children.forEach((child) => {
            if (child.id === 288) {
              // Get the child (in this case, 288 is 'show menu widgets')
              workspaceVisualMenuWidgetsChild = child;
              if (!workspaceVisualMenuWidgetsChild.children) {
                // Create the children array if it doesn't exist
                workspaceVisualMenuWidgetsChild.children = [];
              }
            }
          });
        }
      });

      // Move Widget permissions under Workspace child
      if (workspaceVisualMenuWidgetsChild && subjects.has("Widget")) {
        const widgetSubject = subjects.get("Widget");
        if (widgetSubject.children) {
          workspaceVisualMenuWidgetsChild.children = workspaceVisualMenuWidgetsChild.children.concat(
            widgetSubject.children
          );
          // Store child IDs for later use
          widgetChilds.value = workspaceVisualMenuWidgetsChild.children.map(
            (child) => child.id
          );
        }
        subjects.delete("Widget"); // Remove the 'Widget' subject
      }

      // Return as an array for the Treeselect component
      return Array.from(subjects.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    });

    // Fetch app modules from store
    const appModulesOptions = ref([]);

    onMounted(() => {
      store.dispatch("app_modules/getAll").then(({ data }) => {
        appModulesOptions.value = data.map((app_module) => ({
          text: app_module.name,
          value: app_module.id,
        }));
      });
    });

    const toggleAll = () => {
      if (allSelected.value) {
        selectedPermissions.value = [];
      } else {
        selectedPermissions.value = permissionsOptions.value.flatMap((subject) =>
          subject.children.flatMap((child) =>
            [child.id].concat(
              // IDs of grandchildren, if any
              child.children
                ? child.children.map((grandchild) => grandchild.id)
                : []
            )
          )
        );
      }
    };

    const openGroup = (groupId) => {
        const groupUrl = `${window.location.origin}/#/group?groupId=${groupId}`;
        window.open(groupUrl);
    }

    const validateStep = () => {
      return refFormObserver.value.validate();
    };

    const fillPermissionsFromProfile = (profileId) => {
      if (!profileId || !props.profileOptions.length) return;
      const profileObj = props.profileOptions.find(
        (profile) => profile.id === profileId
      );
      if (profileObj && profileObj.permissions) {
        selectedPermissions.value = profileObj.permissions.map(
          (perm) => perm.id
        );
      }
    };

    watch(
      () => itemData.value.id_profile,
      (newValue, oldValue) => {
        if (newValue !== oldValue && !isUpdatingFromProps.value) {
          fillPermissionsFromProfile(newValue);
          emit("update:itemData", {
            id_profile: newValue,
            permission: selectedPermissions.value,
          });
        }
      },
      { immediate: true }
    );

    watch(
      () => props.propItemData,
      (newValue) => {
        isUpdatingFromProps.value = true;
        selectedPermissions.value = Array.isArray(newValue.permission)
          ? [...newValue.permission]
          : [];
        itemData.value.app_modules = newValue.app_modules || [];
        nextTick(() => {
          isUpdatingFromProps.value = false;
        });
      },
      { deep: true }
    );

    // Watch for changes in selectedPermissions
    watch(
      () => selectedPermissions.value,
      (newValue, oldValue) => {
        if (newValue !== oldValue && !isUpdatingFromProps.value) {
          // Check if any of the widget child IDs are selected but the parent is not
          const childIds = widgetChilds.value;
          const parentId = 288;

          const hasChildSelected = childIds.some((id) =>
            newValue.includes(id)
          );
          const hasParentSelected = newValue.includes(parentId);
          nextTick(() => {
            if (hasChildSelected && !hasParentSelected) {
              if (!selectedPermissions.value.includes(parentId)) {
                newValue.push(parentId);
              }
            }
            emit("update:itemData", { permission: newValue });
          });
        }
        nextTick(() => {
          isUpdatingFromProps.value = false;
        });
      }
    );

    // Watch for changes in app_modules
    watch(
      () => itemData.value.app_modules,
      (newValue, oldValue) => {
        if (newValue !== oldValue && !isUpdatingFromProps.value) {
          emit("update:itemData", { app_modules: newValue });
        }
        nextTick(() => {
          isUpdatingFromProps.value = false;
        });
      }
    );

    return {
      refFormObserver,
      getValidationState,
      itemData,
      selectedPermissions,
      permissionsOptions,
      valueConsistsOf,
      allSelected,
      toggleAll,
      validateStep,
      appModulesOptions,
      openGroup,
    };
  },
};
</script>

<style scoped>
textarea {
  background-color: white;
}

.mt-1 {
  margin-top: 1rem !important;
}

.mt-20 {
  margin-top: 20rem !important;
}
</style>

